import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import { computed } from "vue";
import {
    PageDomainFormSchema,
    type PageDomainFormData,
    type PageDomainDTO,
} from "@/types/PageDomains";
import { usePageDomains } from "./usePageDomains";

type UsePageDomainFormOptions = {
    pageDomain?: Partial<PageDomainDTO>;
    defaultTeamId?: number | null;
    onSuccess?: () => void;
};

export function usePageDomainForm(options?: UsePageDomainFormOptions) {
    const { createPageDomain, updatePageDomain } = usePageDomains();

    const isEditing = computed(() => !!options?.pageDomain?.id);

    const { defineField, handleSubmit, errors, meta, resetForm } =
        useForm<PageDomainFormData>({
            validationSchema: toTypedSchema(PageDomainFormSchema),
            initialValues: {
                domain: options?.pageDomain?.domain ?? "",
                is_default: options?.pageDomain?.is_default ?? false,
                team_id:
                    options?.defaultTeamId ??
                    options?.pageDomain?.team_id ??
                    null,
            },
        });

    // Define fields with eager validation (validates on every change)
    const [domain] = defineField("domain", { validateOnModelUpdate: true });
    const [isDefault] = defineField("is_default", {
        validateOnModelUpdate: true,
    });
    const [teamId] = defineField("team_id", { validateOnModelUpdate: true });

    // Direct access to errors from useForm
    const fieldErrors = computed(() => ({
        domain: errors.value.domain,
        is_default: errors.value.is_default,
        team_id: errors.value.team_id,
    }));

    const isValid = computed(() => meta.value.valid);
    const isDirty = computed(() => meta.value.dirty);

    // Submit handler
    const onSubmit = handleSubmit((values) => {
        const payload = {
            domain: values.domain.toLowerCase().trim(),
            is_default: values.is_default ?? false,
            team_id: values.team_id ?? null,
        };

        if (isEditing.value && options?.pageDomain?.id) {
            const result = updatePageDomain(
                options.pageDomain.id,
                { domain: payload.domain },
                {
                    onSuccess: options?.onSuccess,
                },
            );

            if (!result.success) {
                console.error("Validation failed:", result.errors.flatten());
            }
        } else {
            const result = createPageDomain(payload, {
                onSuccess: options?.onSuccess,
            });

            if (!result.success) {
                console.error("Validation failed:", result.errors.flatten());
            }
        }
    });

    return {
        // Fields (reactive, two-way bindable with v-model)
        domain,
        isDefault,
        teamId,

        // Validation state
        errors: fieldErrors,
        isValid,
        isDirty,

        // Actions
        onSubmit,
        resetForm,

        // State
        isEditing,
    };
}
